/**
 * Minecraft-style inventory and ender-chest grids for the Player Profile.
 *
 * Items arrive already decoded by the game plugin, in the same JSON the dupe item card renders. All
 * names and lore come from players, so they are only ever rendered as text (ItemCard escapes §
 * colour codes into spans). There are no item icon images on this site yet: a slot shows a short
 * name and the amount, and hovering shows the full item card.
 */

import { useState } from "react";
import { prettyMaterial, stripLegacy } from "../minecraft";
import { ItemCard } from "./ItemCard";
import type { Grid, Item } from "../profileApi";

/** Per-slot comparison against the player's current items. */
export type SlotDiff = "added" | "missing" | "changed";

function itemName(item: Item): string {
  const display = typeof item.display_name === "string" ? stripLegacy(item.display_name) : "";
  return display || prettyMaterial(String(item.material ?? "UNKNOWN"));
}

function matches(item: Item | null | undefined, filter: string): boolean {
  if (!item || !filter) return false;
  const needle = filter.toLowerCase();
  if (itemName(item).toLowerCase().includes(needle)) return true;
  if (String(item.material ?? "").toLowerCase().includes(needle)) return true;
  return (item.contents ?? []).some((inner) => matches(inner, filter));
}

function Slot({
  item,
  filter,
  diff,
  onOpenContents,
}: {
  item: Item | null | undefined;
  filter: string;
  diff?: SlotDiff;
  onOpenContents: (item: Item) => void;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  const hasContents = Boolean(item?.contents && item.contents.some(Boolean));
  const dimmed = Boolean(filter) && !matches(item, filter);
  const amount = typeof item?.amount === "number" ? item.amount : 1;

  return (
    <div
      className="mc-slot"
      data-empty={!item}
      data-dim={dimmed}
      data-hit={Boolean(filter) && matches(item, filter)}
      data-diff={diff}
      data-contents={hasContents}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => item && hasContents && onOpenContents(item)}
      tabIndex={item ? 0 : -1}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      role={item ? "button" : undefined}
      aria-label={item ? `${itemName(item)}${amount > 1 ? ` ×${amount}` : ""}` : "Empty slot"}
    >
      {item && (
        <>
          <span className="mc-slot-name">{itemName(item)}</span>
          {amount > 1 && <span className="mc-slot-amount">{amount}</span>}
          {hasContents && <span className="mc-slot-open" aria-hidden="true">⧉</span>}
          {hover && (
            <div className="mc-slot-tip" role="tooltip">
              <ItemCard item={item} />
              {hasContents && <div className="users-note">Click to see what is inside.</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ContentsModal({ item, onClose }: { item: Item; onClose: () => void }): JSX.Element {
  const contents = item.contents ?? [];
  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div className="dialog mc-contents" onMouseDown={(event) => event.stopPropagation()} role="dialog" aria-label="Contents">
        <h2>{itemName(item)}</h2>
        <div className="mc-row mc-row-9">
          {contents.map((inner, index) => (
            <Slot key={index} item={inner} filter="" onOpenContents={() => undefined} />
          ))}
        </div>
        <div className="dialog-buttons">
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const ARMOR: Array<keyof NonNullable<Grid["armor"]>> = ["helmet", "chestplate", "leggings", "boots"];

/** Inventory (armor + offhand, 3×9 main, 1×9 hotbar). `diffs` keys: "armor.helmet", "main.4", … */
export function InventoryGrid({
  grid,
  filter,
  diffs,
}: {
  grid: Grid | undefined;
  filter: string;
  diffs?: Map<string, SlotDiff>;
}): JSX.Element {
  const [open, setOpen] = useState<Item | null>(null);
  const main = Array.from({ length: 27 }, (_, i) => grid?.main?.[i] ?? null);
  const hotbar = Array.from({ length: 9 }, (_, i) => grid?.hotbar?.[i] ?? null);

  return (
    <div className="mc-inventory">
      <div className="mc-side">
        {ARMOR.map((slot) => (
          <Slot key={slot} item={grid?.armor?.[slot] ?? null} filter={filter} diff={diffs?.get(`armor.${slot}`)} onOpenContents={setOpen} />
        ))}
        <div className="mc-gap" />
        <Slot item={grid?.offhand ?? null} filter={filter} diff={diffs?.get("offhand")} onOpenContents={setOpen} />
      </div>
      <div className="mc-main">
        <div className="mc-row mc-row-9">
          {main.map((item, i) => (
            <Slot key={i} item={item} filter={filter} diff={diffs?.get(`main.${i}`)} onOpenContents={setOpen} />
          ))}
        </div>
        <div className="mc-row mc-row-9 mc-hotbar">
          {hotbar.map((item, i) => (
            <Slot key={i} item={item} filter={filter} diff={diffs?.get(`hotbar.${i}`)} onOpenContents={setOpen} />
          ))}
        </div>
      </div>
      {open && <ContentsModal item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Ender chest: 3×9. `diffs` keys: "ender.0" … */
export function EnderGrid({
  items,
  filter,
  diffs,
}: {
  items: Array<Item | null> | undefined;
  filter: string;
  diffs?: Map<string, SlotDiff>;
}): JSX.Element {
  const [open, setOpen] = useState<Item | null>(null);
  const slots = Array.from({ length: 27 }, (_, i) => items?.[i] ?? null);
  return (
    <div className="mc-main">
      <div className="mc-row mc-row-9">
        {slots.map((item, i) => (
          <Slot key={i} item={item} filter={filter} diff={diffs?.get(`ender.${i}`)} onOpenContents={setOpen} />
        ))}
      </div>
      {open && <ContentsModal item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/**
 * Slot-by-slot differences between a snapshot and the current items, from the snapshot's point of
 * view: "added" = in the snapshot, not there now (a rollback would bring it back); "missing" = there
 * now, empty in the snapshot (a rollback would remove it); "changed" = same item, different count.
 */
export function diffInventories(
  snapshot: { inventory?: Grid; enderChest?: Array<Item | null> },
  current: { inventory?: Grid; enderChest?: Array<Item | null> },
): Map<string, SlotDiff> {
  const out = new Map<string, SlotDiff>();
  const compare = (key: string, then: Item | null | undefined, now: Item | null | undefined): void => {
    if (!then && !now) return;
    if (then && !now) out.set(key, "added");
    else if (!then && now) out.set(key, "missing");
    else if (then && now) {
      if (String(then.material) !== String(now.material)) out.set(key, "added");
      else if ((then.amount ?? 1) !== (now.amount ?? 1)) out.set(key, "changed");
    }
  };
  for (let i = 0; i < 27; i++) compare(`main.${i}`, snapshot.inventory?.main?.[i], current.inventory?.main?.[i]);
  for (let i = 0; i < 9; i++) compare(`hotbar.${i}`, snapshot.inventory?.hotbar?.[i], current.inventory?.hotbar?.[i]);
  for (const slot of ARMOR) compare(`armor.${slot}`, snapshot.inventory?.armor?.[slot], current.inventory?.armor?.[slot]);
  compare("offhand", snapshot.inventory?.offhand, current.inventory?.offhand);
  for (let i = 0; i < 27; i++) compare(`ender.${i}`, snapshot.enderChest?.[i], current.enderChest?.[i]);
  return out;
}
